/* Ovaro theme — vanilla JS, no dependencies.
   Structure: helpers · header/menu · cart (drawer + page) · per-section init (product, gallery,
   carousel, fitment, reveal). Section init re-runs on shopify:section:load for the theme editor. */
(() => {
  'use strict';

  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const routes = (window.theme && window.theme.routes) || {};
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Header ------------------------------------------------------------- */
  // Look the header up each time: the theme editor re-renders it when its settings change.
  const onScroll = () => {
    const header = $('[data-header]');
    if (!header) return;
    const preview = document.documentElement.dataset.headerPreview === '1';
    header.classList.toggle('is-scrolled', window.scrollY > 24 || preview);
  };
  window.addEventListener('scroll', onScroll, { passive: true });
  onScroll();

  // In the theme editor, show the header in its scrolled state while its section is selected.
  document.addEventListener('shopify:section:select', (e) => {
    if ($('[data-header]', e.target)) { document.documentElement.dataset.headerPreview = '1'; onScroll(); }
  });
  document.addEventListener('shopify:section:deselect', (e) => {
    if ($('[data-header]', e.target)) { delete document.documentElement.dataset.headerPreview; onScroll(); }
  });

  /* Menu overlay + cart drawer ----------------------------------------- */
  const menu = $('[data-menu]');
  const drawer = $('[data-drawer]');
  const scrim = $('[data-drawer-scrim]');
  let lastFocus = null;

  const setOpen = (el, open, extra) => {
    if (!el) return;
    el.classList.toggle('is-open', open);
    el.setAttribute('aria-hidden', String(!open));
    if (extra) extra.classList.toggle('is-open', open);
    document.body.classList.toggle('no-scroll', open);
    if (open) {
      lastFocus = document.activeElement;
      const target = $('[data-menu-close], [data-drawer-close]', el);
      target && target.focus({ preventScroll: true });
    } else if (lastFocus) {
      lastFocus.focus({ preventScroll: true });
    }
  };
  const openMenu = () => { setOpen(menu, true); $$('[data-menu-open]').forEach((b) => b.setAttribute('aria-expanded', 'true')); };
  const closeMenu = () => { setOpen(menu, false); $$('[data-menu-open]').forEach((b) => b.setAttribute('aria-expanded', 'false')); };
  const openDrawer = () => setOpen(drawer, true, scrim);
  const closeDrawer = () => setOpen(drawer, false, scrim);

  document.addEventListener('click', (e) => {
    const t = e.target;
    if (t.closest('[data-menu-open]')) return openMenu();
    if (t.closest('[data-menu-close]')) return closeMenu();
    if (t.closest('[data-menu] nav a')) return closeMenu();
    if (t.closest('[data-drawer-close]') || t.closest('[data-drawer-scrim]')) return closeDrawer();
    const cartLink = t.closest('[data-cart-open]');
    if (cartLink && drawer && window.location.pathname !== routes.cart) {
      e.preventDefault();
      refreshCart().then(openDrawer);
    }
  });
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    closeMenu();
    closeDrawer();
  });

  /* Cart --------------------------------------------------------------- */
  const onCartPage = () => window.location.pathname === routes.cart;

  async function refreshCart() {
    try {
      const [sec, cart] = await Promise.all([
        fetch(`${routes.root}?sections=cart-drawer`).then((r) => r.json()),
        fetch(routes.cartJs).then((r) => r.json()),
      ]);
      const doc = new DOMParser().parseFromString(sec['cart-drawer'], 'text/html');
      const next = $('[data-drawer-inner]', doc);
      const cur = $('[data-drawer-inner]');
      if (next && cur) cur.innerHTML = next.innerHTML;
      $$('[data-cart-count]').forEach((el) => {
        el.textContent = cart.item_count;
        el.hidden = cart.item_count === 0;
      });
    } catch (err) {
      /* network hiccup: leave the drawer as-is */
    }
  }

  async function changeLine(line, quantity) {
    await fetch(routes.cartChange, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line, quantity }),
    });
    if (onCartPage()) return window.location.reload();
    await refreshCart();
  }

  document.addEventListener('click', (e) => {
    const step = e.target.closest('[data-qty-change]');
    const remove = e.target.closest('[data-line-remove]');
    if (!step && !remove) return;
    const row = e.target.closest('[data-line]');
    const input = $('[data-line-qty]', row);
    const line = Number(row.dataset.line);
    const qty = remove ? 0 : Math.max(0, Number(input.value) + Number(step.dataset.qtyChange));
    changeLine(line, qty);
  });
  document.addEventListener('change', (e) => {
    const input = e.target.closest('[data-line-qty]');
    if (!input) return;
    changeLine(Number(input.closest('[data-line]').dataset.line), Math.max(0, Number(input.value) || 0));
  });

  /* Product page ------------------------------------------------------- */
  function initProduct(section) {
    const form = $('[data-product-form]', section);
    const dataEl = $('[data-product-json]', section);
    if (!form || !dataEl) return;
    const data = JSON.parse(dataEl.textContent);
    const variants = data.variants;
    const fieldsets = $$('[data-option-name]', form);
    const idInput = $('[data-variant-id]', form);
    const addBtn = $('[data-add]', form);
    const addLabel = $('[data-add-label]', form);
    const stickyBtn = $('[data-add-sticky]', section);
    const priceEl = $('[data-price]', section);
    const stickyPrice = $('[data-sticky-price]', section);
    const errorEl = $('[data-form-error]', form);

    const selected = () => fieldsets.map((fs) => ($('input:checked', fs) || {}).value);
    const match = (opts) => variants.find((v) => v.options.every((o, i) => o === opts[i]));

    function updatePills(opts) {
      fieldsets.forEach((fs, i) => {
        $$('input', fs).forEach((input) => {
          const test = opts.slice();
          test[i] = input.value;
          const any = variants.some((v) => v.options.every((o, j) => (j === i ? o === input.value : o === opts[j])) && v.available);
          input.classList.toggle('is-soldout', !any);
        });
        const label = $('[data-option-selected]', fs);
        if (label) label.textContent = opts[i];
      });
    }

    function render(variant, opts) {
      updatePills(opts);
      if (!variant) {
        addBtn.disabled = true;
        addLabel.textContent = data.labels.soldOut;
        return;
      }
      idInput.value = variant.id;
      const priceHtml = (variant.compare ? `<s>${variant.compare}</s>` : '') + `<span>${variant.price}</span>`;
      if (priceEl) priceEl.innerHTML = priceHtml;
      if (stickyPrice) stickyPrice.textContent = variant.price;
      const label = variant.available ? data.labels.add : data.labels.soldOut;
      addBtn.disabled = !variant.available;
      addLabel.textContent = label;
      if (stickyBtn) { stickyBtn.disabled = !variant.available; stickyBtn.textContent = label; }
      if (window.location.pathname.indexOf('/products/') === 0) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      }
      if (variant.media) section.dispatchEvent(new CustomEvent('variant:media', { detail: variant.media, bubbles: true }));
    }

    form.addEventListener('change', (e) => {
      if (!e.target.matches('input[type="radio"]')) return;
      const opts = selected();
      render(match(opts), opts);
    });

    // ?vehicle=Audi preselects the matching Vehicle option (used by the fitment checker)
    const preset = new URLSearchParams(window.location.search).get('vehicle');
    if (preset) selectOption(section, /vehicle/i, preset);
    updatePills(selected());

    $$('[data-qty-step]', form).forEach((btn) => btn.addEventListener('click', () => {
      const input = $('[data-qty]', form);
      input.value = Math.max(1, (Number(input.value) || 1) + Number(btn.dataset.qtyStep));
    }));

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      if (addBtn.disabled) return;
      errorEl.hidden = true;
      addBtn.disabled = true;
      try {
        const res = await fetch(routes.cartAdd, { method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(form) });
        const json = await res.json();
        if (!res.ok) throw new Error(json.description || json.message || 'Could not add to cart');
        await refreshCart();
        if (drawer) openDrawer(); else window.location.href = routes.cart;
      } catch (err) {
        errorEl.textContent = err.message;
        errorEl.hidden = false;
      } finally {
        addBtn.disabled = false;
      }
    });

    const sticky = $('[data-sticky-bar]', section);
    if (sticky && 'IntersectionObserver' in window) {
      new IntersectionObserver(([entry]) => {
        sticky.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0);
      }).observe(addBtn);
    }
  }

  // Selects an option by fieldset name regex + value; returns true if found.
  function selectOption(root, nameRe, value) {
    const fs = $$('[data-option-name]', root).find((f) => nameRe.test(f.dataset.optionName));
    if (!fs) return false;
    const input = $$('input', fs).find((i) => i.value === value);
    if (!input) return false;
    input.checked = true;
    input.dispatchEvent(new Event('change', { bubbles: true }));
    return true;
  }

  /* Gallery ------------------------------------------------------------ */
  function initGallery(root) {
    const gallery = $('[data-gallery]', root);
    if (!gallery) return;
    const view = $('[data-gallery-viewport]', gallery);
    const items = $$('.gallery__item', view);
    const thumbs = $$('[data-thumb]', gallery);
    const go = (i, smooth = true) => {
      const idx = Math.max(0, Math.min(items.length - 1, i));
      view.scrollTo({ left: idx * view.clientWidth, behavior: smooth && !reduceMotion ? 'smooth' : 'auto' });
    };
    const current = () => Math.round(view.scrollLeft / view.clientWidth);
    const prev = $('[data-gallery-prev]', gallery);
    const next = $('[data-gallery-next]', gallery);
    prev && prev.addEventListener('click', () => go(current() - 1));
    next && next.addEventListener('click', () => go(current() + 1));
    thumbs.forEach((t) => t.addEventListener('click', () => go(Number(t.dataset.thumb))));
    view.addEventListener('scroll', () => {
      const i = current();
      thumbs.forEach((t, n) => (n === i ? t.setAttribute('aria-current', 'true') : t.removeAttribute('aria-current')));
    }, { passive: true });
    root.addEventListener('variant:media', (e) => {
      const i = items.findIndex((el) => Number(el.dataset.mediaId) === Number(e.detail));
      if (i > -1) go(i);
    });
  }

  /* Carousel ----------------------------------------------------------- */
  function initCarousel(root) {
    const track = $('[data-carousel-track]', root);
    if (!track) return;
    const slides = $$('[data-slide]', track);
    const quotes = $$('[data-quote]', root);
    const dotsWrap = $('[data-carousel-dots]', root);
    let active = -1;

    // Sound toggle: videos start muted (required for autoplay); the viewer can unmute the active slide.
    const setMuted = (slide, muted) => {
      const v = $('video', slide);
      const btn = $('[data-sound]', slide);
      if (!v || !btn) return;
      v.muted = muted;
      btn.setAttribute('aria-pressed', String(!muted));
      btn.setAttribute('aria-label', muted ? 'Unmute video' : 'Mute video');
      $('[data-sound-off]', btn).hidden = !muted;
      $('[data-sound-on]', btn).hidden = muted;
    };
    slides.forEach((s) => {
      const btn = $('[data-sound]', s);
      if (!btn) return;
      btn.addEventListener('click', () => {
        const v = $('video', s);
        setMuted(s, !v.muted);
        if (!v.muted) v.play().catch(() => {});
      });
    });

    const dots = slides.map((_, i) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.setAttribute('aria-label', `Slide ${i + 1}`);
      b.addEventListener('click', () => go(i));
      dotsWrap && dotsWrap.appendChild(b);
      return b;
    });

    const go = (i) => {
      const s = slides[Math.max(0, Math.min(slides.length - 1, i))];
      if (!s) return;
      track.scrollTo({ left: s.offsetLeft - (track.clientWidth - s.offsetWidth) / 2, behavior: reduceMotion ? 'auto' : 'smooth' });
    };

    const setActive = (i) => {
      if (i === active) return;
      active = i;
      slides.forEach((s, n) => {
        s.classList.toggle('is-active', n === i);
        const v = $('video', s);
        if (!v) return;
        if (n === i) v.play().catch(() => {});
        else { v.pause(); setMuted(s, true); }
      });
      dots.forEach((d, n) => d.setAttribute('aria-current', String(n === i)));
      quotes.forEach((q, n) => q.classList.toggle('is-active', n === i));
    };

    const measure = () => {
      const mid = track.scrollLeft + track.clientWidth / 2;
      let best = 0, dist = Infinity;
      slides.forEach((s, n) => {
        const d = Math.abs(s.offsetLeft + s.offsetWidth / 2 - mid);
        if (d < dist) { dist = d; best = n; }
      });
      setActive(best);
    };
    track.addEventListener('scroll', () => window.requestAnimationFrame(measure), { passive: true });
    window.addEventListener('resize', measure);

    const prev = $('[data-carousel-prev]', root);
    const next = $('[data-carousel-next]', root);
    prev && prev.addEventListener('click', () => go(active - 1));
    next && next.addEventListener('click', () => go(active + 1));
    root.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') go(active - 1);
      if (e.key === 'ArrowRight') go(active + 1);
    });

    $$('video', track).forEach((v) => v.pause());
    measure();
    setActive(Math.max(0, active));
  }

  /* Fitment checker ---------------------------------------------------- */
  function initFitment(root) {
    const select = $('[data-fit-select]', root);
    if (!select) return;
    const result = $('[data-fit-result]', root);
    const status = $('[data-fit-status]', root);
    const note = $('[data-fit-note]', root);
    const apply = $('[data-fit-apply]', root);
    const base = apply.getAttribute('href');

    select.addEventListener('change', () => {
      const opt = select.selectedOptions[0];
      if (!opt || !opt.value) { result.classList.remove('is-visible'); return; }
      const st = opt.dataset.status;
      const title = opt.dataset.title;
      const defaults = {
        fits: `Fits your ${title}`,
        check: title,
        development: `${title}: in development`,
        unsupported: `${title} isn't compatible`,
      };
      status.textContent = opt.dataset.label || defaults[st] || title;
      status.classList.toggle('is-warn', st === 'check' || st === 'development');
      status.classList.toggle('is-bad', st === 'unsupported');
      note.textContent = opt.dataset.note || '';
      const value = opt.dataset.option;
      const canApply = (st === 'fits' || st === 'check') && value;
      apply.hidden = !canApply;
      if (value) apply.setAttribute('href', `${base}${base.includes('?') ? '&' : '?'}vehicle=${encodeURIComponent(value)}`);
      apply.dataset.option = canApply ? value : '';
      result.classList.add('is-visible');
    });

    apply.addEventListener('click', (e) => {
      const productSection = $('[data-product-section]');
      if (!productSection || !apply.dataset.option) return;
      e.preventDefault();
      selectOption(productSection, /vehicle/i, apply.dataset.option);
      productSection.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
    });
  }

  /* Reveal on scroll ----------------------------------------------------- */
  let revealObserver = null;
  function initReveal(root) {
    const els = $$('.reveal:not(.is-in)', root);
    if (!els.length) return;
    if (!('IntersectionObserver' in window) || (window.theme && window.theme.designMode)) {
      els.forEach((el) => el.classList.add('is-in'));
      return;
    }
    revealObserver = revealObserver || new IntersectionObserver((entries) => {
      entries.forEach((en) => {
        if (!en.isIntersecting) return;
        en.target.classList.add('is-in');
        revealObserver.unobserve(en.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });
    els.forEach((el) => revealObserver.observe(el));
  }


  /* Countdown ------------------------------------------------------------ */
  function initCountdown(root) {
    if (root._timer) clearInterval(root._timer);
    const target = new Date(root.dataset.date).getTime();
    const grid = $('[data-countdown-grid]', root);
    const done = $('[data-countdown-done]', root);
    const pad = (n) => String(n).padStart(2, '0');
    const tick = () => {
      const left = Math.max(0, target - Date.now());
      if (Number.isNaN(target) || left === 0) {
        grid.hidden = true;
        done.hidden = false;
        clearInterval(root._timer);
        return;
      }
      grid.hidden = false;
      done.hidden = true;
      $('[data-d]', root).textContent = pad(Math.floor(left / 864e5));
      $('[data-h]', root).textContent = pad(Math.floor(left / 36e5) % 24);
      $('[data-m]', root).textContent = pad(Math.floor(left / 6e4) % 60);
      $('[data-s]', root).textContent = pad(Math.floor(left / 1e3) % 60);
    };
    tick();
    root._timer = setInterval(tick, 1000);
  }

  /* Face switcher ---------------------------------------------------------- */
  function initFaces(root) {
    const tabs = $$('[data-face-tab]', root);
    const panels = $$('[data-face-panel]', root);
    const show = (i) => {
      tabs.forEach((t, n) => t.setAttribute('aria-selected', String(n === i)));
      panels.forEach((p, n) => {
        p.classList.toggle('is-active', n === i);
        const v = $('video', p);
        if (v) { n === i ? v.play().catch(() => {}) : v.pause(); }
      });
    };
    tabs.forEach((t, i) => t.addEventListener('click', () => show(i)));
    show(0);
  }

  /* A/B audio compare ------------------------------------------------------ */
  function initAB(root) {
    const clips = $$('[data-ab-clip]', root);
    const picks = $$('[data-ab-pick]', root);
    const soundBtn = $('[data-ab-sound]', root);
    let current = 0;
    let muted = true;
    const stage = $('[data-ab-stage]', root);
    const side = window.matchMedia('(min-width: 901px)'); // desktop: clips sit side by side, click one to play it
    const vid = (i) => $('video', clips[i]);
    const paintSound = () => {
      soundBtn.setAttribute('aria-pressed', String(!muted));
      soundBtn.setAttribute('aria-label', muted ? 'Unmute' : 'Mute');
      $('[data-sound-off]', soundBtn).hidden = !muted;
      $('[data-sound-on]', soundBtn).hidden = muted;
    };
    const apply = (i, keepTime) => {
      const from = vid(current);
      clips.forEach((c, n) => c.classList.toggle('is-active', n === i));
      picks.forEach((b, n) => b.setAttribute('aria-pressed', String(n === i)));
      if (stage) stage.dataset.active = String(i);
      clips.forEach((c, n) => { if (c.hasAttribute('role')) c.setAttribute('aria-pressed', String(n === i)); });
      clips.forEach((c, n) => {
        const v = vid(n);
        if (!v) return;
        if (n === i) {
          if (keepTime && from && from !== v && Number.isFinite(from.currentTime)) v.currentTime = from.currentTime % (v.duration || Infinity);
          v.muted = muted;
          v.play().catch(() => {});
        } else {
          v.muted = true;
          v.pause();
        }
      });
      current = i;
    };
    // The pill's highlight slides under the selected label.
    const thumb = $('[data-ab-thumb]', root);
    const moveThumb = () => {
      const active = picks[current];
      if (!thumb || !active) return;
      thumb.style.width = `${active.offsetWidth}px`;
      thumb.style.transform = `translateX(${active.offsetLeft}px)`;
    };
    const choose = (i) => {
      if (i === current || i < 0 || i >= clips.length) return;
      muted = false; // choosing a clip is a gesture: play it with sound
      paintSound();
      apply(i, true);
      moveThumb();
    };
    picks.forEach((b, i) => b.addEventListener('click', () => choose(i)));

    // Desktop: click (or Enter/Space) on a clip to play it with sound. Phones keep the pill and swipe.
    const syncLayout = () => {
      clips.forEach((c, n) => {
        if (side.matches) {
          c.setAttribute('role', 'button');
          c.setAttribute('tabindex', '0');
          c.setAttribute('aria-pressed', String(n === current));
          const cap = $('.ab__cap', c);
          if (cap) c.setAttribute('aria-label', cap.textContent.trim());
        } else {
          ['role', 'tabindex', 'aria-pressed', 'aria-label'].forEach((a) => c.removeAttribute(a));
        }
      });
      moveThumb();
    };
    clips.forEach((c, i) => {
      c.addEventListener('click', (e) => {
        if (!side.matches || e.target.closest('button')) return;
        choose(i);
      });
      c.addEventListener('keydown', (e) => {
        if (!side.matches || e.target !== c || (e.key !== 'Enter' && e.key !== ' ')) return;
        e.preventDefault();
        choose(i);
      });
    });
    if (side.addEventListener) side.addEventListener('change', syncLayout);

    // Swipe the video itself: the clips cross-fade (the video never slides) and the pill follows.
    if (stage) {
      let sx = 0, sy = 0, down = false;
      stage.addEventListener('pointerdown', (e) => {
        if (side.matches || e.target.closest('button')) return;
        down = true; sx = e.clientX; sy = e.clientY;
      });
      const end = (e) => {
        if (!down) return;
        down = false;
        const dx = e.clientX - sx, dy = e.clientY - sy;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.4) {
          choose(clips.length > 1 ? (current + 1) % clips.length : current);
        }
      };
      stage.addEventListener('pointerup', end);
      stage.addEventListener('pointercancel', () => { down = false; });
    }
    window.addEventListener('resize', moveThumb);
    soundBtn.addEventListener('click', () => {
      muted = !muted;
      paintSound();
      const v = vid(current);
      if (v) { v.muted = muted; v.play().catch(() => {}); }
    });
    paintSound();
    apply(0, false);
    syncLayout();
    if (thumb) { thumb.style.transition = 'none'; moveThumb(); setTimeout(() => { thumb.style.transition = ''; }, 120); }
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(moveThumb);
  }

  /* Stats count-up --------------------------------------------------------- */
  function initCount(root) {
    $$('[data-count]:not([data-counted])', root).forEach((el) => {
      const m = el.textContent.trim().match(/^([^\d]*)([\d][\d,]*\.?\d*)(.*)$/);
      if (!m || reduceMotion || !('IntersectionObserver' in window)) return;
      const [, pre, numStr, post] = m;
      const decimals = (numStr.split('.')[1] || '').length;
      const end = parseFloat(numStr.replace(/,/g, ''));
      const useComma = numStr.includes(',');
      const fmt = (n) => {
        const t = n.toFixed(decimals);
        return pre + (useComma ? Number(t).toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals }) : t) + post;
      };
      el.dataset.counted = '1';
      el.textContent = fmt(0);
      new IntersectionObserver(([entry], obs) => {
        if (!entry.isIntersecting) return;
        obs.disconnect();
        const start = performance.now();
        const dur = 1400;
        const step = (now) => {
          const k = Math.min(1, (now - start) / dur);
          el.textContent = fmt(end * (1 - Math.pow(1 - k, 3)));
          if (k < 1) requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
      }, { threshold: 0.4 }).observe(el);
    });
  }


  /* Image hotspots -------------------------------------------------------- */
  function initHotspots(root) {
    if (root.dataset.hsReady === '1') return;
    root.dataset.hsReady = '1';
    const stages = $$('[data-stage]', root);
    if (!stages.length) return;
    const dots = $$('[data-hotspot]', root);
    const popups = $$('[data-popup]', root);
    const popupFor = (dot) => document.getElementById(dot.getAttribute('aria-controls'));

    // Mobile: the popup becomes a bottom sheet. It is moved to <body> while open so no scroll container or
    // transformed parent can clip it, and it fades away when the page or the carousel scrolls.
    const mq = window.matchMedia('(max-width: 640px)');
    const home = new WeakMap();
    let stopWatching = null;
    const unportal = (pop) => {
      const h = home.get(pop);
      if (!h) return;
      home.delete(pop);
      setTimeout(() => {   // wait for the fade-out before putting it back
        if (pop.classList.contains('is-open')) { home.set(pop, h); return; }
        h.parent.insertBefore(pop, h.next && h.next.parentNode === h.parent ? h.next : null);
      }, 350);
    };
    const hide = (dot) => {
      dot.setAttribute('aria-expanded', 'false');
      const p = popupFor(dot);
      if (p) { p.classList.remove('is-open'); p.setAttribute('aria-hidden', 'true'); unportal(p); }
    };
    const closeAll = (except) => {
      dots.forEach((d) => { if (d !== except) hide(d); });
      if (!except && stopWatching) { stopWatching(); stopWatching = null; }
    };
    const watchScroll = (dot) => {
      if (stopWatching) stopWatching();
      const row = dot.closest('.hotspots__row');
      // The card stays up while you scroll past: it only closes once the dot itself has left the screen
      // (scrolled away vertically, or swiped out of view in the row).
      const onScroll = () => {
        const r = dot.getBoundingClientRect();
        const cx = r.left + r.width / 2;
        const offScreen = r.bottom < 0 || r.top > window.innerHeight;
        const rr = row ? row.getBoundingClientRect() : null;
        const offRow = rr && (cx < rr.left || cx > rr.right);
        if (offScreen || offRow) closeAll();
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      row && row.addEventListener('scroll', onScroll, { passive: true });
      stopWatching = () => {
        window.removeEventListener('scroll', onScroll);
        row && row.removeEventListener('scroll', onScroll);
      };
    };

    const place = (dot, pop) => {
      const x = parseFloat(getComputedStyle(dot).getPropertyValue('--x')) || 50;
      const y = parseFloat(getComputedStyle(dot).getPropertyValue('--y')) || 50;
      const side = dot.dataset.side || 'auto';
      pop.classList.remove('pos-above', 'pos-below', 'pos-left', 'pos-right', 'al-left', 'al-center', 'al-right');
      if (side === 'left' || side === 'right') { pop.classList.add(`pos-${side}`); return; }
      const vertical = side === 'above' || side === 'below' ? side : (y > 55 ? 'above' : 'below');
      const align = x < 24 ? 'left' : x > 76 ? 'right' : 'center';
      pop.classList.add(`pos-${vertical}`, `al-${align}`);
    };

    dots.forEach((dot) => {
      dot.addEventListener('click', (e) => {
        e.stopPropagation();
        const pop = popupFor(dot);
        if (!pop) return;
        const open = dot.getAttribute('aria-expanded') === 'true';
        closeAll(open ? null : dot);
        if (open) { hide(dot); if (stopWatching) { stopWatching(); stopWatching = null; } return; }
        place(dot, pop);
        if (mq.matches) {
          if (!home.has(pop) && pop.parentNode !== document.body) {
            home.set(pop, { parent: pop.parentNode, next: pop.nextSibling });
            document.body.appendChild(pop);
          }
          pop.classList.add('hs-popup--sheet');
          pop.style.setProperty('--g-blur', getComputedStyle(root).getPropertyValue('--g-blur') || '18px');
          watchScroll(dot);
        } else {
          pop.classList.remove('hs-popup--sheet');
        }
        dot.setAttribute('aria-expanded', 'true');
        // let the sheet mount before fading it in
        requestAnimationFrame(() => { pop.classList.add('is-open'); pop.setAttribute('aria-hidden', 'false'); });
      });
    });
    popups.forEach((pop) => {
      pop.addEventListener('click', (e) => e.stopPropagation());
      const close = $('[data-close]', pop);
      close && close.addEventListener('click', () => {
        const dot = dots.find((d) => d.getAttribute('aria-controls') === pop.id);
        closeAll(); dot && dot.focus({ preventScroll: true });
      });
    });
    document.addEventListener('click', () => closeAll());
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeAll(); });

    // Theme-editor helper: click an image to read its position as percentages.
    if (window.theme && window.theme.designMode) {
      stages.forEach((stage) => {
        const media = $('.hotspots__media', stage);
        let marker = null;
        media && media.addEventListener('click', (e) => {
          const r = stage.getBoundingClientRect();
          const px = Math.min(100, Math.max(0, ((e.clientX - r.left) / r.width) * 100));
          const py = Math.min(100, Math.max(0, ((e.clientY - r.top) / r.height) * 100));
          const split = (v) => { const whole = Math.round(v); return [whole, Math.round((v - whole) * 10) / 10]; };
          const [xw, xf] = split(px), [yw, yf] = split(py);
          if (!marker) { marker = document.createElement('div'); marker.className = 'hotspots__helper'; stage.appendChild(marker); }
          marker.style.left = `${px}%`; marker.style.top = `${py}%`;
          marker.innerHTML = `<span>Left/right ${xw} (fine ${xf >= 0 ? '+' : ''}${xf}) &middot; Up/down ${yw} (fine ${yf >= 0 ? '+' : ''}${yf})</span>`;
        });
      });
    }
  }


  /* Image hotspots: the mobile blurred backdrop follows whichever image is showing ------ */
  function initHotspotsBg(root) {
    if (root.dataset.hsBgReady === '1') return;
    const bg = $('[data-hs-bg]', root);
    const row = $('.hotspots__row', root);
    if (!bg || !row) return;
    const layers = $$('[data-hs-bg-layer]', bg);
    const items = $$('.hotspots__item', row);
    if (!layers.length || !items.length) return;
    root.dataset.hsBgReady = '1';
    let current = -1;
    const set = (i) => {
      if (i === current) return;
      current = i;
      layers.forEach((l, n) => l.classList.toggle('is-active', n === i));
    };
    // The most visible image in the row wins; the backdrop cross-fades (it never slides with the swipe).
    const measure = () => {
      const rr = row.getBoundingClientRect();
      let best = 0, bestV = -1;
      items.forEach((it, n) => {
        const r = it.getBoundingClientRect();
        const v = Math.max(0, Math.min(r.right, rr.right) - Math.max(r.left, rr.left));
        if (v > bestV + 1) { bestV = v; best = n; }
      });
      set(Math.min(best, layers.length - 1));
    };
    row.addEventListener('scroll', () => window.requestAnimationFrame(measure), { passive: true });
    window.addEventListener('resize', measure);
    measure();
  }


  /* Sticky glass product bar --------------------------------------------- */
  function initGbar(root) {
    const bar = $('[data-gbar]', root);
    if (!bar || bar.dataset.ready === '1') return;
    bar.dataset.ready = '1';
    // the product page has its own sticky add-to-cart bar; never show two
    if (document.querySelector('[data-sticky-bar]')) { bar.hidden = true; return; }
    if (window.theme && window.theme.designMode) { bar.classList.add('is-visible'); return; }
    const after = Math.max(1, parseInt(bar.dataset.after, 10) || 1);
    const sections = $$('main > .shopify-section');
    const anchor = sections[Math.min(after, sections.length) - 1];
    let past = false;
    const blockers = new Set();
    const apply = () => bar.classList.toggle('is-visible', past && blockers.size === 0);
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        ticking = false;
        past = anchor ? anchor.getBoundingClientRect().bottom < 0 : window.scrollY > 400;
        apply();
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    // hide while the real buy box (or the footer) is already on screen
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries) => {
        entries.forEach((en) => { en.isIntersecting ? blockers.add(en.target) : blockers.delete(en.target); });
        apply();
      }, { threshold: 0.15 });
      $$('[data-product-section], .footer').forEach((el) => io.observe(el));
    }
    // on a page with a buy box, the button scrolls to it instead of leaving the page
    $$('[data-gbar-link]', bar).forEach((a) => a.addEventListener('click', (e) => {
      const target = document.getElementById('buy');
      if (!target) return;
      e.preventDefault();
      target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
    }));
  }

  /* Display waitlist: save the chosen car as a customer tag --------------- */
  function initWaitlist(root) {
    $$('[data-waitlist]', root).forEach((form) => {
      if (form.dataset.ready === '1') return;
      form.dataset.ready = '1';
      const tags = $('[data-tags]', form), car = $('[data-car]', form);
      if (!tags || !car) return;
      const sync = () => { tags.value = car.value ? `${tags.dataset.base}, ${car.value}` : tags.dataset.base; };
      car.addEventListener('change', sync);
      form.addEventListener('submit', sync);
    });
  }

  /* Wave dividers: a line that ripples with scroll speed -------------------- */
  const waves = { items: new Set(), vel: 0, phase: 0, last: window.scrollY, raf: 0 };
  function drawWave(el, amp) {
    const cycles = parseFloat(el.style.getPropertyValue('--wave-freq')) || 3;
    const path = $('path', el);
    const fill = el.hasAttribute('data-wave-fill');
    const N = 64;
    const baseY = fill ? 60 : 50;
    let d = '';
    for (let i = 0; i <= N; i++) {
      const t = i / N;
      const env = fill ? 1 : Math.sin(Math.PI * t);             // lines taper to flat at both edges; filled waves run the full width
      const y = baseY + Math.sin(t * cycles * Math.PI * 2 + waves.phase) * amp * env;
      d += `${i === 0 ? 'M' : 'L'}${(t * 1000).toFixed(1)} ${y.toFixed(2)}`;
    }
    const edge = $('[data-wave-edge]', el);
    if (edge) edge.setAttribute('d', d);                       // the open crest line, before the fill is closed
    if (fill) d += 'L1000 100L0 100Z';
    path.setAttribute('d', d);
  }
  function waveTick() {
    const y = window.scrollY;
    const dy = Math.min(Math.abs(y - waves.last), 90);
    waves.last = y;
    waves.vel += (dy - waves.vel) * 0.16;
    waves.phase += 0.018 + waves.vel * 0.012;
    waves.items.forEach((el) => {
      const base = parseFloat(el.style.getPropertyValue('--wave-amp')) || 0;
      const react = parseFloat(el.style.getPropertyValue('--wave-react')) || 0;
      drawWave(el, Math.min(32, base + react * waves.vel * 0.75));
    });
    waves.raf = waves.items.size ? window.requestAnimationFrame(waveTick) : 0;
  }
  function initWave(root) {
    $$('[data-wave]', root).forEach((el) => {
      if (el.dataset.ready === '1') return;
      el.dataset.ready = '1';
      const base = parseFloat(el.style.getPropertyValue('--wave-amp')) || 0;
      drawWave(el, base);
      if (reduceMotion || !('IntersectionObserver' in window)) return;
      new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) { waves.items.add(el); if (!waves.raf) waves.raf = window.requestAnimationFrame(waveTick); }
        else waves.items.delete(el);
      }, { rootMargin: '80px' }).observe(el);
    });
  }


  /* Footer curve: match the color behind the corners to the section above -------- */
  function initFooterCurve(root) {
    const wrap = $('[data-footer-wrap]', root);
    if (!wrap || wrap.dataset.behind !== 'auto') return;
    const main = document.getElementById('main');
    if (!main) return;
    // Only full-width backgrounds count (a section's), not a button or card inside an app block
    const solid = (el) => { const c = getComputedStyle(el).backgroundColor; return c && c !== 'rgba(0, 0, 0, 0)' && c !== 'transparent' && el.getBoundingClientRect().width >= window.innerWidth * 0.9 ? c : null; };
    const find = (el) => {
      if (!el) return null;
      const own = solid(el);
      if (own) return own;
      for (const kid of el.children) { const hit = find(kid); if (hit) return hit; }
      return null;
    };
    const apply = () => {
      const last = main.lastElementChild;
      const color = (last && find(last)) || null;
      if (color) wrap.style.setProperty('--fw-bg', color);
    };
    apply();
    window.addEventListener('load', apply);
  }

  /* Browser bars: iOS 26 Safari tints its top/bottom bars from the body background (and ignores
     theme-color). The page defaults to light via CSS; a page that ends on a dark section starts dark,
     and any page turns dark once the footer reaches the bottom edge. */
  function initChrome() {
    const main = document.getElementById('main');
    if (!main || !main.lastElementChild) return;
    const root = document.documentElement;
    const meta = document.querySelector('meta[name="theme-color"]');
    const last = main.lastElementChild;
    const tone = last.matches('[class*="tone-"]') ? last : last.querySelector('[class*="tone-"]');
    const pageDark = !tone || /\btone-dark\b/.test(tone.className);
    const footer = document.querySelector('footer.footer');
    const paint = (bg) => {
      if (bg) root.style.setProperty('--chrome-bg', bg); else root.style.removeProperty('--chrome-bg');
      if (meta) meta.setAttribute('content', bg || meta.dataset.base);
    };
    if (meta) meta.dataset.base = meta.getAttribute('content');
    const darkBg = getComputedStyle(pageDark ? (tone || main) : (footer || main)).backgroundColor;
    let state = null;
    const check = () => {
      const atFooter = !!footer && footer.getBoundingClientRect().top <= window.innerHeight - 2;
      const dark = pageDark || atFooter;
      if (dark === state) return;
      state = dark;
      paint(dark ? darkBg : '');
    };
    window.addEventListener('scroll', check, { passive: true });
    window.addEventListener('resize', check);
    check();
  }
  try { initChrome(); } catch (e) { /* cosmetic only */ }

  /* Collection sort ------------------------------------------------------ */
  document.addEventListener('change', (e) => {
    const sort = e.target.closest('[data-sort]');
    if (!sort) return;
    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', sort.value);
    url.searchParams.delete('page');
    window.location.href = url.toString();
  });

  /* Boot ----------------------------------------------------------------- */
  function initSection(root) {
    if (root.matches && root.matches('[data-product-section]')) initProduct(root);
    $$('[data-product-section]', root).forEach(initProduct);
    initGallery(root);
    $$('[data-carousel]', root).forEach(initCarousel);
    if (root.matches && root.matches('[data-carousel]')) initCarousel(root);
    $$('[data-countdown]', root).forEach(initCountdown);
    $$('[data-faces]', root).forEach(initFaces);
    $$('[data-ab]', root).forEach(initAB);
    initCount(root);
    $$('[data-hotspots]', root).forEach(initHotspots);
    $$('[data-hotspots]', root).forEach(initHotspotsBg);
    if (root.matches && root.matches('[data-hotspots]')) initHotspotsBg(root);
    if (root.matches && root.matches('[data-hotspots]')) initHotspots(root);
    initGbar(root);
    initFooterCurve(root);
    initWaitlist(root);
    initWave(root);
    $$('[data-fitment]', root).forEach(initFitment);
    if (root.matches && root.matches('[data-fitment]')) initFitment(root);
    initReveal(root);
  }

  const boot = () => initSection(document);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  document.addEventListener('shopify:section:load', (e) => { initSection(e.target); onScroll(); });
})();
